import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import CompareArrowsOutlinedIcon from "@mui/icons-material/CompareArrowsOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Button } from "@/components/common/Button";
import { Stack } from "@/components/common/Stack";
import { ConfirmDialog } from "@/components/main/utils/ConfirmDialog";
import {
  StatusChip,
  type StatusChipTone,
} from "@/components/common/StatusChip";
import { Callout } from "@/components/common/Callout";
import { SegmentedControl } from "@/components/common/SegmentedControl";
import { TabToggle } from "@/components/common/TabToggle";
import { Tooltip } from "@/components/common/Tooltip";
import { Chip } from "@/components/common/Chip";
import {
  useApplyDesign,
  useDesignApp,
  useDesignFeedback,
  useProviders,
  useRenameDraftColumn,
  useSaveDesignSetup,
  useSetDraftUnit,
  useRun,
  useStopApp,
  useSyncDesign,
} from "@/lib/api/apps/hooks";
import { AppApiError } from "@/lib/api/apps/apps";
import type {
  ResearchOnTables,
  DraftFeedback,
  DraftTable,
  DraftVersion,
  TableFeedback,
  Verdict,
} from "@/lib/api/apps/types";
import { plural } from "@/components/common/setUtils";
import { usd, usdMicros } from "../app-format";
import { UsageCard } from "./usage-card";
import { DraftProgressPanel } from "./draft-progress";
import { AppPageHead, AppPageShell, Composer, NameTitle } from "../page-shell";
import { DraftGraph, draftEmbedModel } from "./draft-graph";
import { PaneDivider } from "./pane-divider";
import { OverlayScroll } from "@/components/common/OverlayScroll";
import { MetaAddButton } from "@/components/main/datasets/DatasetMetaSection";
import { NewModelOverlay } from "@/components/main/overview/NewModelOverlay";
import { useCreateModel } from "@/components/main/utils/models/useCreateModel";
import { useStoredWidth } from "@/components/hooks/useStoredWidth";
import {
  DraftChangeList,
  DraftLinkOverlay,
  DraftTableSection,
  DraftTableTab,
  DraftingCard,
  DraftReviewList,
  MarkChips,
} from "./draft-table";
import { CostLimit } from "./cost-limit";
import { SpaceModelPicker, useEditableModels } from "./target-picker";
import { ModelMark } from "../picker";
import { SpaceDot } from "@/components/main/spaces/SpaceDot";
import {
  spaceColor,
  useOrgContextStore,
} from "@/components/main/stores/orgContextStore";
import { ModelChip } from "./model-field";
import {
  chooseModel,
  clock,
  DRAFT_POLL_MS,
  modelLabel,
  progressOf,
  researchLine,
  useNow,
} from "./draft-model";
import { semanticLabel } from "./type-label";
import { describeChanges, markLines, reviewProse } from "./draft-changes";
import {
  applyOutcome,
  isMarked,
  linkEnds,
  removedIn,
  saysAnything,
  shownVerdict,
} from "./draft-helpers";
import { useUnsentMarks } from "./unsent-marks";
import { RefusalCallout } from "@/components/authentication/consent/RefusalCallout";

// The table tabs' strip and the gap under it, so the graph's box starts level with the columns' box.
const CHANGES = "__changes";
const UNDER_TABS = "calc(var(--item-height) + var(--spacing-sm))";
const REVIEW = "__review";

/** Where a version came from or what Apply did with it, for the chip on its hover. */
const versionStatus = (
  v: DraftVersion,
): { tone: StatusChipTone; text: string } | null => {
  if (v.origin === "model") return { tone: "muted", text: "From the model" };
  if (v.origin === "edit" && !v.apply)
    return { tone: "muted", text: "Edited by you" };
  switch (v.apply?.state) {
    case "applied":
      return { tone: "accent", text: "Applied" };
    case "partial":
      return {
        tone: "warning",
        text: `${plural(v.apply.not_applied?.length ?? 0, "change")} not applied`,
      };
    case "cancelled":
      return { tone: "warning", text: "Not applied" };
    default:
      return null;
  }
};

const roundCost = (v: DraftVersion) => (v.cost ? usdMicros(v.cost.micros) : "");

/** A design app (`/apps/:app_id`): the agent's versions of the tables. Marks
 *  and notes become the next version; Apply makes the last one in the
 *  target model, as the owner. */
export const DesignPage: React.FC<{
  appId: string;
  /** From the list, so the head shows before the app arrives. */
  name?: string;
}> = ({ appId, name }) => {
  const navigate = useNavigate();
  const draft = useDesignApp(appId);
  const feedback = useDesignFeedback(appId);
  const accept = useApplyDesign(appId);
  const sync = useSyncDesign(appId);
  // Once per visit: tables someone changed in the model show as the latest version.
  useEffect(() => {
    sync.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId]);
  const rename = useRenameDraftColumn(appId);
  const setUnit = useSetDraftUnit(appId);
  const [graphWidth, setGraphWidth] = useStoredWidth("design-graph-width");
  const [unitFor, setUnitFor] = useState<{
    tableId: string;
    column: string;
  } | null>(null);
  const stop = useStopApp(appId);
  // A space picked before its model, so the model list can open on it.
  const [pendingSpace, setPendingSpace] = useState<string | null>(null);
  const [panelTab, setPanelTab] = useState<"approve" | "request" | null>(null);
  const spaceById = useOrgContextStore((s) => s.spaceById);
  // A model made from the empty picker becomes the target.
  const [naming, setNaming] = useState(false);
  const { createModel } = useCreateModel();
  const work = useRef<HTMLDivElement>(null);
  const page = useRef<HTMLDivElement>(null);
  const saveTarget = useSaveDesignSetup(appId);
  const targets = useEditableModels();
  const [at, setAt] = useState<number | null>(null);
  const [lit, setLit] = useState<string | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [link, setLink] = useState<{ tableId: string; column: string } | null>(
    null,
  );
  const providers = useProviders(draft.data?.org_id ?? null);
  // The next round's model: the person's pick, else whatever drafted the last version.
  const [nextPick, setNextPick] = useState<{
    provider: string;
    model: string;
  } | null>(null);
  const [modelOpen, setModelOpen] = useState(false);

  const versions = draft.data?.versions ?? [];
  const last = versions.length - 1;
  const latest = versions[last];
  const [marks, setMarks] = useUnsentMarks(appId, latest?.n, latest?.carried);
  // A new version opens on what it changed, not on the table the review left open.
  useEffect(() => {
    setPicked(null);
    setLink(null);
  }, [latest?.n]);
  // Asking for a change opens Request changes; a Lock alone asks for nothing.
  useEffect(() => {
    if (saysAnything(marks.tables, marks.note)) setPanelTab(null);
  }, [marks]);
  const index = at ?? last;
  const version = versions[index];
  const previous =
    versions.find((v) => v.n === version?.from_n) ??
    (index > 0 ? versions[index - 1] : undefined);
  const busy = feedback.isPending || !!draft.data?.drafting;
  const now = useNow(busy);
  const runId = draft.data?.run?.run_id ?? null;
  const round = useRun(appId, runId, busy, DRAFT_POLL_MS);
  const roundProgress = round.data
    ? progressOf(round.data.run, round.data.events)
    : null;
  // Only a round watched on this visit reports how it ended.
  const watched = useRef<string | null>(null);
  if (busy && runId) watched.current = runId;
  const roundError =
    !busy &&
    round.data?.run.run_id === watched.current &&
    roundProgress?.error &&
    roundProgress.error.code !== "stopped"
      ? new AppApiError(
          0,
          roundProgress.error.code,
          roundProgress.error.message,
        )
      : null;
  const applied = draft.data?.applied_version ?? null;
  const editable = index === last && !busy && !accept.isPending;
  const midCommit =
    accept.error instanceof AppApiError &&
    accept.error.code === "table_mid_commit"
      ? ((accept.error.body.tables as string[] | undefined) ?? [])
      : [];
  const removed = useMemo(
    () => (version ? removedIn(version, previous) : []),
    [version, previous],
  );

  if (draft.isLoading && !draft.data) {
    return (
      <AppPageShell maxWidth="72rem" fill>
        <AppPageHead
          trail={[{ label: "Applications", to: "/apps" }]}
          title={name ?? ""}
          kind="Design"
        />
      </AppPageShell>
    );
  }
  if (draft.data && versions.length === 0) {
    return (
      <AppPageShell maxWidth="46rem">
        <AppPageHead
          trail={[{ label: "Applications", to: "/apps" }]}
          title={draft.data.name}
          kind="Design"
        />
        <DraftProgressPanel
          progress={roundProgress}
          model={
            providers.data
              ? modelLabel(
                  providers.data,
                  draft.data.provider,
                  draft.data.model,
                )
              : "The agent"
          }
          version={1}
          onStop={() => stop.mutate()}
          stopping={stop.isPending || (stop.isSuccess && busy)}
        />
      </AppPageShell>
    );
  }
  if (draft.error || !draft.data || !version) {
    return (
      <div className="flex h-full w-full items-center justify-center px-6">
        <Callout variant="error" title="This app could not be opened">
          {(draft.error as Error | null)?.message ?? "no such app"}
        </Callout>
      </div>
    );
  }

  const fb = marks.tables;
  const tableFeedback = (id: string): TableFeedback =>
    fb[id] ?? { verdict: null, drop_columns: [], note: "" };
  // An earlier version shows the marks it was sent with; the latest, the unsent ones.
  const sentWith = index < last ? versions[index + 1].marks : undefined;
  // An earlier version shows its tables as drafted; what it was then asked for heads the next version's changes.
  const marksOn = (id: string): TableFeedback =>
    index < last
      ? { verdict: null, drop_columns: [], note: "" }
      : tableFeedback(id);
  const setTable = (id: string, patch: Partial<TableFeedback>) =>
    setMarks({
      ...marks,
      tables: { ...fb, [id]: { ...tableFeedback(id), ...patch } },
    });
  const verdictOf = (id: string, v: Verdict) => {
    const current = tableFeedback(id);
    const next = current.verdict === v ? null : v;
    const settled = next === "keep" || next === "drop";
    setTable(id, {
      verdict: next,
      drop_columns: settled ? [] : current.drop_columns,
      column_notes: settled ? {} : current.column_notes,
    });
  };
  // Marks name their columns, so a rename carries them over once it lands.
  const renameColumn = (table: DraftTable, from: string, to: string) => {
    const name = to.trim();
    if (name === from) return true;
    if (!name || table.columns.some((c) => c.name === name)) return false;
    rename.mutate(
      { table: table.id, column: from, name },
      {
        onSuccess: () => {
          const f = tableFeedback(table.id);
          const notes = { ...f.column_notes };
          if (from in notes) {
            notes[name] = notes[from];
            delete notes[from];
          }
          setTable(table.id, {
            drop_columns: f.drop_columns.map((c) => (c === from ? name : c)),
            column_notes: notes,
          });
        },
      },
    );
    return true;
  };
  const noteColumn = (id: string, column: string, text: string) =>
    setTable(id, {
      column_notes: { ...tableFeedback(id).column_notes, [column]: text },
    });
  const toggleColumn = (id: string, column: string) => {
    const current = tableFeedback(id);
    setTable(id, {
      drop_columns: current.drop_columns.includes(column)
        ? current.drop_columns.filter((c) => c !== column)
        : [...current.drop_columns, column],
    });
  };

  const said = saysAnything(fb, marks.note);
  const pending = markLines(marks, versions[last]?.tables ?? []);
  const send = () => {
    feedback.mutate(
      { ...marks, provider: choice?.provider.id, model: choice?.model.id },
      { onSuccess: () => setAt(null) },
    );
  };
  const acceptIt = () => accept.mutate();
  const openTarget = () => navigate(`/canvas/${draft.data?.target_model_id}`);
  const app = draft.data;
  const onTargetPicked = (modelId: string) => {
    setPendingSpace(null);
    if (modelId !== app.target_model_id)
      saveTarget.mutate({ app, change: { target_model_id: modelId } });
  };
  const before = versions[last - 1];
  const goneElsewhere = (latest?.carried_gone ?? []).filter(
    (g) => !latest?.tables.some((t) => g.startsWith(`${t.name}.`)),
  );
  type BarAct = {
    label: string;
    icon?: React.ReactNode;
    onClick: (e: React.MouseEvent<HTMLElement>) => void;
  };
  const apply = (label: string): BarAct => ({
    label,
    icon: <SyncOutlinedIcon className="icon-sm" />,
    onClick: acceptIt,
  });
  const open: BarAct = { label: "Open the model", onClick: openTarget };
  const approve: BarAct =
    applied === null
      ? {
          label: `Create ${plural(versions[last].tables.length, "table")}`,
          icon: <TableChartOutlinedIcon className="icon-sm" />,
          onClick: acceptIt,
        }
      : apply(`Apply v${versions[last].n}`);
  // What the bar says about the latest version, and its one main act.
  const [barStatus, primary] = ((): [string | null, BarAct] => {
    if (applied === null) return [null, approve];
    // Applied before Apply kept the ids of what it made.
    if (!versions.some((v) => v.apply || v.origin === "model"))
      return ["The tables this design made could not be found.", open];
    if (midCommit.length)
      return [(accept.error as Error).message, apply("Try again")];
    if (latest?.apply?.state === "partial")
      return [applyOutcome(latest), apply("Apply the rest")];
    if (latest?.origin === "model")
      return [
        [
          before?.apply?.state === "cancelled"
            ? `v${before.n} was not applied: its tables changed in the model. v${latest.n} is the model now${latest.carried ? ", with your marks" : ""}.`
            : "The tables changed in the model; this is how they are now.",
          applyOutcome(before) && `v${before?.n}: ${applyOutcome(before)}`,
          ...goneElsewhere,
        ]
          .filter(Boolean)
          .join(" "),
        open,
      ];
    if (latest?.apply?.state === "applied")
      return [applyOutcome(latest) || null, open];
    return [
      null,
      latest?.origin === "edit" ? apply("Apply manual edits") : approve,
    ];
  })();
  const act = index === last && primary !== open;
  const target = targets.find(
    (m) => m.model_id === draft.data?.target_model_id,
  );
  const targetSpace = target ? spaceById(target.space_id ?? null) : undefined;
  const destination = target && (
    <span className="inline-flex items-center gap-1.5 text-fg">
      {targetSpace && (
        <>
          <SpaceDot color={spaceColor(targetSpace)} />
          {targetSpace.name}
          <span className="text-fg-muted">›</span>
        </>
      )}
      <ModelMark model={target} />
      {target.name}
    </span>
  );
  const madeTables = versions.find((v) => v.n === applied)?.tables ?? [];
  // Only the first Apply creates the tables; a later one changes them.
  const appliedBefore = versions.some((v) => v.n < (applied ?? 0) && v.apply);
  const approvedNow = applied !== null && !act;
  const runsOn = (e: unknown) =>
    e instanceof AppApiError && e.code === "tables_in_use"
      ? ((e.body.runs as ResearchOnTables[] | undefined) ?? [])
      : [];
  const draftInUse = runsOn(feedback.error);
  const applyInUse = runsOn(accept.error);
  const inUse = draftInUse.length ? draftInUse : applyInUse;
  const failure =
    (draftInUse.length ? null : feedback.error) ??
    roundError ??
    (midCommit.length || applyInUse.length ? null : accept.error) ??
    saveTarget.error ??
    rename.error ??
    setUnit.error;
  const costs = versions.flatMap((v) => (v.cost ? [v.cost] : []));
  // Rounds from before the split have no breakdown to show, so no card.
  const split = costs.length > 0 && costs.every((c) => c.usage);
  const changes = previous
    ? describeChanges(previous, version, semanticLabel)
    : null;
  // What goes with the note, which is the field itself: the next round's marks, or what an earlier version was sent with.
  const reviewLines = (
    index === last
      ? pending
      : sentWith
        ? markLines(sentWith, version.tables)
        : []
  ).filter((l) => l.kind !== "note");
  // Opened from beside the note, the review takes the columns' place, so the graph stays in view.
  const showReview = picked === REVIEW && reviewLines.length > 0;
  const reviewed = index === last ? marks : sentWith;
  const prose = (counted: boolean) =>
    reviewed
      ? reviewProse(reviewed, version.tables, { sent: index !== last, counted })
      : "";
  // The marks shown with the note: the next round's, or the ones an earlier version was sent with.
  const marksShown = index === last ? fb : (sentWith?.tables ?? {});
  const hasMarks = version.tables.some((t) => isMarked(marksShown[t.id]));
  const clearTable = (id: string) => {
    const { [id]: _cleared, ...rest } = fb;
    setMarks({ ...marks, tables: rest });
  };
  // A version that changed something opens on what changed.
  // Every version after the first has its changes view (what was asked, what it did);
  // one that changed something opens on it.
  const showChanges =
    !!previous &&
    (picked === CHANGES || (picked === null && !!changes?.length));
  const shown =
    showReview || showChanges
      ? null
      : (version.tables.find((t) => t.id === picked) ?? version.tables[0]);
  // The choice for the latest version; marks open Request changes.
  const tab = panelTab ?? (approvedNow ? "approve" : "request");
  const pick = (id: string) => {
    setLink(null);
    setPicked(id);
  };
  const lastCost = versions[last].cost;
  const usedPick = lastCost?.provider
    ? { provider: lastCost.provider, model: lastCost.model }
    : { provider: draft.data.provider, model: draft.data.model };
  const choice = providers.data
    ? chooseModel(providers.data, nextPick ?? usedPick)
    : null;
  const modelName = (provider: string, model: string) =>
    providers.data ? modelLabel(providers.data, provider, model) : model;
  const sent = feedback.isPending ? feedback.variables : undefined;
  const drafting =
    busy && roundProgress?.state === "running" ? roundProgress : null;
  // Once the round has made a call, its usage names its model, also on a page opened mid-round.
  const roundUsage = drafting ? round.data?.run.usage[0] : undefined;
  const draftingIds = roundUsage
    ? { provider: roundUsage.provider, model: roundUsage.model }
    : sent?.provider && sent.model
      ? { provider: sent.provider, model: sent.model }
      : choice
        ? { provider: choice.provider.id, model: choice.model.id }
        : null;
  const draftingModel = draftingIds
    ? modelName(draftingIds.provider, draftingIds.model)
    : "The agent";

  const requested = (sent: DraftFeedback, on: DraftTable[]) =>
    [
      Object.values(sent.tables).some(isMarked) &&
        reviewProse({ ...sent, note: "" }, on, { sent: true }),
      sent.note.trim() && `“${sent.note.trim()}”`,
    ]
      .filter(Boolean)
      .join(" ");
  // What asked for a version: the brief for the first, a round's marks and note after.
  const askedFor = (x: DraftVersion, i: number) => {
    if (i === 0) return app.brief.trim();
    const on = versions.find((v) => v.n === x.from_n) ?? versions[i - 1];
    return x.marks && on ? requested(x.marks, on.tables) : "";
  };
  // The same request above a version's changes, in the chips it was made with.
  const askedView = (x: DraftVersion, i: number) => {
    if (i === 0)
      return (
        app.brief.trim() && (
          <p className="m-0 text-fg-regular">{app.brief.trim()}</p>
        )
      );
    const on = versions.find((v) => v.n === x.from_n) ?? versions[i - 1];
    if (!x.marks || !on) return null;
    const note = x.marks.note.trim();
    return (
      <>
        {Object.values(x.marks.tables).some(isMarked) && (
          <span className="flex flex-wrap gap-1.5">
            <MarkChips
              marks={x.marks.tables}
              tables={on.tables}
              onHover={setLit}
              sent
              max={on.tables.length}
            />
          </span>
        )}
        {note && <p className="m-0 text-fg-regular">“{note}”</p>}
      </>
    );
  };
  const created = madeTables.slice(0, 4);
  // What approving this version does, or what stands in its way.
  const approveLead =
    barStatus ??
    (applied === null
      ? `Pick the model for v${versions[last].n}'s ${plural(versions[last].tables.length, "table")}.`
      : `Applying v${versions[last].n} brings its changes to ${target?.name ?? "the model"}.`);
  // The composer's inset, for its content and its button alike.
  const approveBody = (
    <div className="flex-1 flex flex-col gap-2 p-2">
      {approvedNow ? (
        <>
          <div className="flex items-center gap-1.5 min-h-[var(--control-height-md)] text-fg-muted-regular">
            <CheckCircleOutlinedIcon className="icon-sm text-success" />
            {appliedBefore
              ? `v${applied} applied to`
              : `${plural(madeTables.length, "table")} created in`}
            {destination ?? " its target model"}
          </div>
          {madeTables.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {created.map((t) => (
                <Chip
                  key={t.id}
                  size="sm"
                  label={
                    <span className="inline-flex items-center gap-1">
                      <TableChartOutlinedIcon className="icon-xs text-fg-muted" />
                      {t.name}
                    </span>
                  }
                  onClick={() => pick(t.id)}
                />
              ))}
              {madeTables.length > created.length && (
                <Tooltip
                  title={madeTables
                    .slice(created.length)
                    .map((t) => t.name)
                    .join(", ")}
                >
                  <span>
                    <Chip
                      size="sm"
                      label={`+${madeTables.length - created.length}`}
                    />
                  </span>
                </Tooltip>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          <p className="m-0 flex items-start gap-1.5 text-fg-muted-regular">
            {midCommit.length > 0 && (
              <LockOutlinedIcon
                sx={{ fontSize: 14 }}
                className="text-warning"
              />
            )}
            {approveLead}
          </p>
          {applied === null && editable && targets.length === 0 ? (
            <span className="flex items-center gap-2 text-fg-muted-regular">
              No model you can edit yet
              <MetaAddButton
                label="New model"
                onClick={() => setNaming(true)}
              />
            </span>
          ) : applied === null && (editable || accept.isPending) ? (
            <SpaceModelPicker
              disabled={!editable}
              models={targets}
              value={{
                space: pendingSpace ?? target?.space_id ?? null,
                model:
                  pendingSpace && pendingSpace !== target?.space_id
                    ? null
                    : (target?.model_id ?? null),
              }}
              onChange={({ space, model }) => {
                setPendingSpace(space);
                if (model) onTargetPicked(model);
              }}
            />
          ) : (
            destination && (
              <span className="flex items-center min-h-[var(--control-height-md)]">
                {destination}
              </span>
            )
          )}
        </>
      )}
      {approvedNow && barStatus && (
        <p className="m-0 text-fg-muted-regular-small">{barStatus}</p>
      )}
      <div className="mt-auto flex justify-end">
        {act ? (
          <Button
            icon={primary.icon}
            disabled={!editable || !app.target_model_id}
            loading={accept.isPending || saveTarget.isPending}
            onClick={primary.onClick}
          >
            {primary.label}
          </Button>
        ) : (
          <Button onClick={openTarget}>
            {target ? `Open ${target.name}` : "Open the model"}
            <ArrowForwardOutlinedIcon className="icon-sm" />
          </Button>
        )}
      </div>
    </div>
  );
  // The choice for this version: Approve, or ask for changes; only the open tab's act shows.
  const panel = (
    <div className="flex-none flex flex-col gap-[var(--spacing-sm)]">
      {index === last && (
        <div className="flex-none flex items-center item-height">
          <TabToggle<"approve" | "request">
            ariaLabel={`Next step for v${version.n}`}
            value={tab}
            onChange={setPanelTab}
            options={[
              { value: "approve", label: approvedNow ? "Approved" : "Approve" },
              {
                value: "request",
                label: (
                  <span className="inline-flex items-center gap-1.5">
                    Request changes
                    {said && (
                      <span
                        className="size-1.5 rounded-full bg-accent"
                        aria-label="Not sent yet"
                      />
                    )}
                  </span>
                ),
              },
            ]}
          />
        </div>
      )}
      <div className="min-h-36 flex flex-col rounded-app border border-border">
        {index === last && tab === "approve" ? (
          approveBody
        ) : (
          <Composer
            bare
            rows={1}
            grow
            top={
              hasMarks && (
                <MarkChips
                  marks={marksShown}
                  tables={version.tables}
                  onGo={pick}
                  onClear={editable ? clearTable : undefined}
                  onMore={() => setPicked(REVIEW)}
                  onHover={setLit}
                  sent={index !== last}
                />
              )
            }
            value={index === last ? marks.note : (sentWith?.note.trim() ?? "")}
            onChange={(e) => setMarks({ ...marks, note: e.target.value })}
            onKeyDown={(e) => {
              if (
                e.key === "Enter" &&
                (e.metaKey || e.ctrlKey) &&
                said &&
                editable
              )
                send();
            }}
            disabled={!editable}
            placeholder="Add a note for the agent (optional)"
            aria-label="Note for the agent"
            bar={
              <>
                {index === last && choice && providers.data && (
                  <ModelChip
                    catalog={providers.data}
                    choice={choice}
                    onPick={(provider, model) =>
                      setNextPick({ provider, model })
                    }
                    menuOpen={modelOpen}
                    onMenuOpen={setModelOpen}
                    tooltip="Drafts the next round"
                  />
                )}
                <span className="flex-1" />
                <Tooltip title={said ? prose(true) : null}>
                  <span>
                    <Button
                      icon={<SendOutlinedIcon className="icon-sm" />}
                      disabled={!said || !editable}
                      loading={busy}
                      onClick={send}
                    >
                      Draft v{versions[last].n + 1}
                    </Button>
                  </span>
                </Tooltip>
              </>
            }
          />
        )}
      </div>
    </div>
  );

  return (
    <AppPageShell ref={page} maxWidth="72rem" fill>
      <AppPageHead
        trail={[{ label: "Applications", to: "/apps" }]}
        title={
          <NameTitle
            name={app.name}
            onRename={(name) =>
              saveTarget.mutateAsync({ app, change: { name } })
            }
          />
        }
        kind="Design"
        aside={
          <>
            {/* What the rounds have spent of the limit; each version names its model on its own hover. */}
            <span className="ml-auto inline-flex items-baseline gap-1.5 text-fg-muted-regular-small">
              <Tooltip
                title={
                  split ? (
                    <UsageCard
                      costs={costs}
                      title={`${plural(costs.length, "round")} so far`}
                    />
                  ) : null
                }
              >
                <span className="font-mono">
                  {draft.data.spent_micros === undefined
                    ? usd(draft.data.spent_cents)
                    : usdMicros(draft.data.spent_micros)}
                </span>
              </Tooltip>
              <CostLimit app={draft.data} />
            </span>
          </>
        }
      />

      {/* One version: which it is and what changed, then its tables and their graph. */}
      <section
        ref={work}
        className="flex-1 min-h-0 flex flex-col gap-2 -mt-2"
        style={
          {
            "--graph-width": graphWidth === null ? "24rem" : `${graphWidth}px`,
          } as React.CSSProperties
        }
      >
        <div className="flex-none flex items-center gap-3 flex-wrap text-fg-muted-regular-small">
          {busy && (
            <span className="tab-option tab-option-inactive inline-flex items-center gap-1.5 whitespace-nowrap">
              <span
                className="size-1.5 rounded-full bg-accent animate-pulse motion-reduce:animate-none"
                aria-hidden="true"
              />
              v{versions[last].n + 1}
              <span className="font-mono text-fg-muted text-small">
                drafting · {clock(now - (drafting?.started_at_ms ?? now))}
              </span>
            </span>
          )}
          {/* Each version names on hover the model that drafted it and what it cost, and the applied one wears a check. */}
          <span className="h-6 flex-none">
            <SegmentedControl<number>
              value={index}
              onChange={(i) => {
                // The table on screen stays on screen where the version has it.
                setLink(null);
                setAt(i === last ? null : i);
              }}
              options={versions
                .map((x, i) => {
                  const model =
                    x.cost && modelName(x.cost.provider, x.cost.model);
                  const status = versionStatus(x);
                  const asked = askedFor(x, i);
                  const facts = (
                    <span className="flex flex-col gap-1 max-w-80">
                      <span className="inline-flex items-center gap-2">
                        {[model, roundCost(x)].filter(Boolean).join(" · ")}
                        {status && (
                          <StatusChip tone={status.tone}>
                            {status.text}
                          </StatusChip>
                        )}
                      </span>
                      {asked && (
                        <span className="text-fg-muted-regular-small max-h-40 overflow-y-auto">
                          {asked}
                        </span>
                      )}
                    </span>
                  );
                  return {
                    value: i,
                    label: `v${x.n}`,
                    warn: x.apply?.state === "cancelled",
                    mark: (x.n === applied ||
                      (i === last && applied === null)) && (
                      <span className="text-mono-small font-normal">
                        {x.n === applied ? "Applied" : "Draft"}
                      </span>
                    ),
                    interactiveTooltip: !!asked,
                    tooltip: x.cost?.usage ? (
                      <UsageCard costs={[x.cost]} title={facts} />
                    ) : model || x.cost || status || asked ? (
                      facts
                    ) : undefined,
                  };
                })
                .reverse()}
            />
          </span>
        </div>

        <div className="flex-1 min-h-0 grid gap-y-4 grid-cols-[minmax(0,1fr)] grid-rows-[minmax(0,1fr)_auto] lg:gap-y-0 lg:grid-rows-[minmax(0,1fr)] lg:grid-cols-[minmax(0,1fr)_2rem_clamp(16rem,var(--graph-width),calc(100%_-_18rem))]">
          <div className="flex flex-col gap-[var(--spacing-sm)] min-h-0">
            {busy ? (
              <DraftingCard
                progress={drafting}
                model={draftingModel}
                version={versions[last].n + 1}
                onStop={() => stop.mutate()}
                stopping={stop.isPending || (stop.isSuccess && busy)}
              />
            ) : (
              <>
                {/* The token list's drawn thumb, sideways: a native bar would lift these tabs off the box below. */}
                <OverlayScroll
                  axis="both"
                  frameClassName="flex-none min-w-0 item-height"
                >
                  <div className="flex items-center gap-4 item-height">
                    {/* A view of the version, not a table: styled as the version switcher, before the tables. */}
                    {previous && (
                      <span className="h-6 flex-none">
                        <SegmentedControl<string | null>
                          value={showChanges ? CHANGES : null}
                          onChange={() => pick(CHANGES)}
                          options={[
                            {
                              value: CHANGES,
                              label: "Changes",
                              icon: (
                                <CompareArrowsOutlinedIcon className="icon-sm" />
                              ),
                              tooltip: `Changes from v${previous.n}`,
                            },
                          ]}
                        />
                      </span>
                    )}
                    <TabToggle
                      ariaLabel="Tables"
                      value={showReview ? REVIEW : (shown?.id ?? CHANGES)}
                      onChange={pick}
                      options={[
                        ...version.tables.map((t) => ({
                          value: t.id,
                          label: (
                            <span
                              className="inline-flex items-center"
                              onMouseEnter={() => setLit(t.id)}
                              onMouseLeave={() => setLit(null)}
                            >
                              <DraftTableTab
                                table={t}
                                mark={shownVerdict(marksOn(t.id))}
                                locked={midCommit.includes(t.id)}
                              />
                            </span>
                          ),
                        })),
                      ]}
                    />
                  </div>
                </OverlayScroll>
                {shown ? (
                  <DraftTableSection
                    key={shown.id}
                    table={shown}
                    onVerdict={(v) => verdictOf(shown.id, v)}
                    tables={version.tables}
                    notice={
                      midCommit.includes(shown.id)
                        ? `${shown.name} has uncommitted changes in the canvas, so nothing was applied. Commit or discard them there, then try again.`
                        : undefined
                    }
                    gone={(version.carried_gone ?? []).filter((g) =>
                      g.startsWith(`${shown.name}.`),
                    )}
                    feedback={marksOn(shown.id)}
                    editable={editable}
                    onToggleColumn={(c) => toggleColumn(shown.id, c)}
                    onNote={(n) => setTable(shown.id, { note: n })}
                    onNoteColumn={(c, n) => noteColumn(shown.id, c, n)}
                    onRenameColumn={(c, n) => renameColumn(shown, c, n)}
                    linkOpenFor={
                      link?.tableId === shown.id ? link.column : null
                    }
                    onOpenLink={(c) => {
                      setLink((open) =>
                        open?.tableId === shown.id && open.column === c.name
                          ? null
                          : { tableId: shown.id, column: c.name },
                      );
                    }}
                    unitOpenFor={
                      unitFor?.tableId === shown.id ? unitFor.column : null
                    }
                    onOpenUnit={(c) =>
                      setUnitFor({ tableId: shown.id, column: c.name })
                    }
                    onCloseUnit={() => setUnitFor(null)}
                    onApplyUnit={(c, assignment) =>
                      setUnit.mutate({
                        table: shown.id,
                        column: c.name,
                        unit_uri: assignment?.stored_unit_uri ?? null,
                        scale_exponent: assignment?.scale_exponent ?? 0,
                      })
                    }
                  />
                ) : showChanges ? (
                  <DraftChangeList
                    asked={askedView(version, index)}
                    before={previous?.n}
                    changes={changes ?? []}
                    tables={[...version.tables, ...removed]}
                    onOpen={pick}
                  />
                ) : (
                  reviewed && (
                    <DraftReviewList
                      marks={reviewed.tables}
                      tables={version.tables}
                      title={index === last ? "Feedback" : "Feedback sent"}
                      version={version.n}
                      prose={prose(false)}
                      onGo={pick}
                      onHover={setLit}
                      onClose={() => setPicked(null)}
                    />
                  )
                )}
              </>
            )}
            {/* Only the latest version is decided on; an earlier one is read. */}
            {index === last && panel}
          </div>
          <PaneDivider
            label="Graph width"
            onResize={setGraphWidth}
            top={busy ? undefined : UNDER_TABS}
          />
          <aside
            className={`flex flex-col min-h-0 ${busy ? "" : "lg:mt-[calc(var(--item-height)+var(--spacing-sm))]"}`}
          >
            <DraftGraph
              model={draftEmbedModel(version.tables)}
              highlighted={lit}
              selected={shown?.id ?? null}
              onPick={(id) => id && pick(id)}
            >
              {link &&
                (() => {
                  const ends = linkEnds(
                    version.tables,
                    link.tableId,
                    link.column,
                  );
                  const from = version.tables.find(
                    (t) => t.id === link.tableId,
                  );
                  return from && ends.length > 0 ? (
                    <DraftLinkOverlay
                      from={{ table: from.name, column: link.column }}
                      ends={ends}
                      onGo={pick}
                      onHover={setLit}
                      onClose={() => setLink(null)}
                    />
                  ) : null;
                })()}
            </DraftGraph>
          </aside>
        </div>
      </section>

      <NewModelOverlay
        open={naming}
        target={{ spaceId: null }}
        anchorRef={work}
        paneRef={page}
        createModel={async (target, made) => {
          const outcome = await createModel(target, made);
          if (outcome.ok) onTargetPicked(String(outcome.model.model_id));
          return outcome;
        }}
        onClose={() => setNaming(false)}
      />

      <RefusalCallout error={failure} title="That did not go through" />
      <ConfirmDialog
        open={inUse.length > 0}
        title="These tables are being worked on"
        message={
          <Stack gap="sm">
            {inUse.map((run, i) => (
              <span key={i}>{researchLine(run)}</span>
            ))}
            <span>
              {draftInUse.length
                ? "A new draft now may interfere with that work."
                : "Applying now changes these tables while it works on them."}
            </span>
          </Stack>
        }
        confirmText={draftInUse.length ? "Draft anyway" : "Apply anyway"}
        onConfirm={() => {
          if (!draftInUse.length) accept.mutate(true);
          else if (feedback.variables)
            feedback.mutate(
              { ...feedback.variables, interfere: true },
              { onSuccess: () => setAt(null) },
            );
        }}
        onCancel={() => {
          feedback.reset();
          accept.reset();
        }}
      />
      {draft.data.apply_note && (
        <Callout variant="warning" title="Not every table was made">
          {draft.data.apply_note}
        </Callout>
      )}
    </AppPageShell>
  );
};
